import { useEffect, useRef, useState } from 'react';
import { WebView, WebViewMessageEvent } from 'react-native-webview';
import i18n from 'i18n-js';

import { ImageDocumentStoreItem, ImageDocumentStoreItemWithAngle } from '@v2/components/imageHandling/types/types.d';

import { convertAngle } from '@v2/components/imageHandling/helpers';
import { CropperWebProps } from '@v2/components/imageHandling/components/Cropper/components/CropperWeb';
import { getDocumentById } from '@reducers/search';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { parseAndAddUrlsToImagesDocuments } from '@reducers/v2/imageHandlingV2/thunks';

interface UseManageCropperWebParams
  extends Pick<CropperWebProps, 'image' | 'angle' | 'onDelete' | 'onBack' | 'onSave' | 'shouldSaveOnDoneClickIfNotDirty' | 'shouldLoadDocument' | 'isNewImage'> {}

export const useManageCropperWeb = ({
  image,
  angle: angleFromProps,
  onBack,
  onSave,
  onDelete,
  shouldSaveOnDoneClickIfNotDirty,
  shouldLoadDocument,
  isNewImage = false,
}: UseManageCropperWebParams) => {
  const webView = useRef<WebView | null>(null);
  const [loadedImage, setLoadedImage] = useState<ImageDocumentStoreItem | null>(null);
  const [showChooseCropModeModalOpen, setShowChooseCropModeModalOpen] = useState<boolean>(false);
  const [pendingCropData, setPendingCropData] = useState<any>(null);

  const imageToWorkWith = shouldLoadDocument ? loadedImage : image;

  const dispatch = useAppDispatch();

  const [showDiscardChangesPopup, setShowDiscardChangesPopup] = useState<boolean>(false);

  const triggerDiscardChangesPopup = () => setShowDiscardChangesPopup((prev) => !prev);

  useEffect(() => {
    if (!shouldLoadDocument || image.documentId === loadedImage?.documentId) {
      return;
    }
    (async () => {
      const documentData = await dispatch(getDocumentById(image.documentId));
      if (documentData?.length) {
        // Parse the document with URLs before returning
        const parsedResult = await dispatch(
          parseAndAddUrlsToImagesDocuments({
            imagesDocuments: [documentData[0]],
          })
        );

        if (parseAndAddUrlsToImagesDocuments.fulfilled.match(parsedResult)) {
          const parsedData = parsedResult.payload?.[0];
          if (parsedData) {
            setLoadedImage({ ...parsedData, customRotate: image.customRotate });
          }
        }
      }
    })();
  }, [shouldLoadDocument, image.documentId, loadedImage?.documentId]);

  // Track if webview is loaded
  const [isWebViewLoaded, setIsWebViewLoaded] = useState(false);

  const sendMessageToWebView = () => {
    if (webView && webView.current && imageToWorkWith) {
      const crop = imageToWorkWith?.cropSettings?.find((cs) => cs.pageNumber === imageToWorkWith.pageNumber);
      const angleForUncroppedImage = imageToWorkWith?.customRotate?.[imageToWorkWith?.pageNumber] || 0;
      const angleForCroppedImage = convertAngle((crop?.rotationAngle || 0) + angleForUncroppedImage);
      const angle = angleFromProps ?? (imageToWorkWith?.isCropped ? angleForCroppedImage : angleForUncroppedImage);

      const originalImageWidth = angle === 90 || angle === 270 ? imageToWorkWith?.heightOriginal : imageToWorkWith?.widthOriginal;
      const originalImageHeight = angle === 90 || angle === 270 ? imageToWorkWith?.widthOriginal : imageToWorkWith?.heightOriginal;

      // Get existing crop coordinates if image is already cropped
      // For uncropped images, we want to select the full image, so we don't pass specific coordinates
      // Let the HTML cropper determine the full image dimensions
      const cropCoordinates = imageToWorkWith?.isCropped
        ? {
            x: imageToWorkWith?.x || 0,
            y: imageToWorkWith?.y || 0,
            width: imageToWorkWith?.width || originalImageWidth,
            height: imageToWorkWith?.height || originalImageHeight,
            // Pass original dimensions for delta calculation in HTML
            originalImageWidth: originalImageWidth,
            originalImageHeight: originalImageHeight,
          }
        : {
            // For uncropped images, pass undefined to let HTML cropper auto-size
            x: undefined,
            y: undefined,
            width: undefined,
            height: undefined,
            originalImageWidth: undefined,
            originalImageHeight: undefined,
          };

      const messageData = {
        ...(imageToWorkWith ?? {}),
        ...cropCoordinates,
        angle,
        showDelete: Boolean(onDelete),
        shouldSaveOnDoneClickIfNotDirty,
        // Add URL support while maintaining backward compatibility
        imageUrl: imageToWorkWith?.url || imageToWorkWith?.urlSmall,
        imageUrlOriginal: imageToWorkWith?.originUrl,
        translation: {
          back: i18n.t('Cancel'),
          header: i18n.t('ADJUST'),
          ready: i18n.t('Done'),
          delete: i18n.t('Remove Photo'),
        },
      };

      webView.current.postMessage(JSON.stringify(messageData));
    }
  };

  // Effect to send message when imageToWorkWith becomes available
  useEffect(() => {
    if (isWebViewLoaded && imageToWorkWith) {
      sendMessageToWebView();
    }
  }, [isWebViewLoaded, imageToWorkWith, angleFromProps]);

  const onLoad = () => {
    setIsWebViewLoaded(true);
    // Don't send message here - let the useEffect handle it
    // This prevents duplicate messages when shouldLoadDocument is false
  };

  const processCropData = (cropData: any) => {
    if (!cropData || !imageToWorkWith) return;

    // Convert cropper coordinates to ratios (0-1) based on loaded image
    const ratioX = cropData.x / cropData.loadedImageWidth;
    const ratioY = cropData.y / cropData.loadedImageHeight;
    const ratioWidth = cropData.width / cropData.loadedImageWidth;
    const ratioHeight = cropData.height / cropData.loadedImageHeight;

    // Always use real original dimensions for final calculations
    const realOriginalWidth = imageToWorkWith?.widthOriginal || cropData.loadedImageWidth;
    const realOriginalHeight = imageToWorkWith?.heightOriginal || cropData.loadedImageHeight;

    // Calculate display dimensions (swapped for 90°/270°) for coordinate calculation
    const angle = cropData.angle || 0;
    const displayOriginalWidth = angle === 90 || angle === 270 ? realOriginalHeight : realOriginalWidth;
    const displayOriginalHeight = angle === 90 || angle === 270 ? realOriginalWidth : realOriginalHeight;

    const finalX = ratioX * displayOriginalWidth;
    const finalY = ratioY * displayOriginalHeight;
    const finalWidth = ratioWidth * displayOriginalWidth;
    const finalHeight = ratioHeight * displayOriginalHeight;

    // Update image document with correctly converted crop coordinates
    const updatedImage: ImageDocumentStoreItemWithAngle = {
      ...(imageToWorkWith ?? ({} as ImageDocumentStoreItem)),
      angle: cropData.angle,
      base64: cropData.uri,
      x: finalX,
      y: finalY,
      width: finalWidth,
      height: finalHeight,
      // Update original dimensions if they were missing
      widthOriginal: realOriginalWidth,
      heightOriginal: realOriginalHeight,
      isProcessing: true,
    };

    onSave(updatedImage);
  };

  const onMessage = async (event: WebViewMessageEvent) => {
    try {
      if (event && event.nativeEvent) {
        if (event.nativeEvent.data === 'delete') {
          return onDelete?.();
        }

        const obj = JSON.parse(event.nativeEvent.data);

        if (obj.back) {
          if (obj.changed) {
            return triggerDiscardChangesPopup();
          } else {
            return onBack();
          }
        }
        
        if (event.nativeEvent.data !== 'unchanged') {
          // If it's a new image, process immediately without showing the modal
          if (isNewImage) {
            processCropData(obj);
          } else {
            setPendingCropData(obj);
            setShowChooseCropModeModalOpen(true);
          }
          return;
        }
      }
    } catch (e) {
      console.log('Error webCropper onMessage:', event, e);
    }
  };

  return {
    showDiscardChangesPopup,
    triggerDiscardChangesPopup,
    webView,
    onMessage,
    onLoad,
    showChooseCropModeModalOpen,
    setShowChooseCropModeModalOpen,
    pendingCropData,
    setPendingCropData,
    processCropData,
  };
};
